// src/AnimationWrapper.tsx

import React, { useRef, useEffect, useState } from 'react';

// Define el tipo para la dirección de la animación
type Direction = 'up' | 'left' | 'right';

interface AnimationWrapperProps {
    children: React.ReactNode;
    delay?: number; // Retraso en ms para el efecto escalonado
    direction?: Direction; // Dirección desde donde "entra" el contenido
    className?: string; // Para clases adicionales en el contenedor
}

/**
 * Función auxiliar para generar las clases de transición de Tailwind.
 * Define la posición inicial (fuera de la vista) y la final (en la vista).
 */
const getTransitionClasses = (direction: Direction, isVisible: boolean): string => {
    const baseClasses = 'transition-all duration-700 ease-out';
    
    // Estado final: Visible
    if (isVisible) {
        return `${baseClasses} opacity-100 translate-x-0 translate-y-0`;
    }

    // Estado inicial: Oculto y Desplazado
    switch (direction) {
        case 'up':
            return `${baseClasses} opacity-0 translate-y-12`;
        case 'left':
            return `${baseClasses} opacity-0 -translate-x-12`;
        case 'right':
            return `${baseClasses} opacity-0 translate-x-12`;
        default:
            return `${baseClasses} opacity-0 translate-y-12`;
    }
};

const AnimationWrapper: React.FC<AnimationWrapperProps> = ({ 
    children, 
    delay = 0, 
    direction = 'up', 
    className = '' 
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const elementRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
            const currentElement = elementRef.current; // <--- 1. Copiamos el valor aquí

            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setTimeout(() => {
                            setIsVisible(true);
                        }, delay);
                        observer.unobserve(entry.target);
                    }
                },
                { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
            );

            if (currentElement) {
                observer.observe(currentElement); // <--- 2. Usamos la copia
            }

            return () => {
                if (currentElement) {
                    observer.unobserve(currentElement); // <--- 3. Usamos la copia al limpiar
                }
            };
        }, [delay]);

    return (
        <div 
            ref={elementRef} 
            className={`${className} ${getTransitionClasses(direction, isVisible)}`}
        >
            {children}
        </div>
    );
};

export default AnimationWrapper;